import React, { useState, useMemo } from "react";
import intl from "react-intl-universal";
import styled from "styled-components";
import { mdiContentDuplicate, mdiDelete, mdiCloseCircle, mdiPencil, mdiCalculator, mdiAlertOutline } from "@mdi/js";
import { useLocation } from "wouter";
import commonUtils from "../../../script/utils/commonUtils";

import entryRest from "../../../rest/entryRest";
import calculationRest from "../../../rest/calculationRest";

import { EntryProvider } from "../../contexts/EntryContext";
import { ValidationProvider } from "../../contexts/ValidationContext";

import { Svg } from "../../components/Svg";
import { Tooltip } from "../../components/Tooltip";
import { Confirm } from "../../components/Confirm";
import { WattwaechterDialog } from "./WattwaechterDialog";
import { DropdownCaret, Option } from "../../components/DropdownCaret";

const MenuCellStyle = styled.td`
    >div {
        width: 50px;
        display: flex;
        gap: 10px;
        height: 12px;
        margin-top: -3px;
    }
`;

const RightCellStyle = styled.td`
    text-align: right;
`;

const LeftCellStyle = styled.td`
    text-align: left;
`;

const nf = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 4, maximumFractionDigits: 4, useGrouping: false });
const df = new Intl.DateTimeFormat("de-DE", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });

export const EntryItem = props => {

    const { entry, user } = props;
    const [, setLocation] = useLocation();
    const [showWattwaechterDialog, setShowWattwaechterDialog] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    const formatted = useMemo(() => {
        const { name, siteNum, user, calculatedAt, progress, queueOrder, calcSetting } = entry;
        const { username } = user;
        const calculatedAtStr = calculatedAt ? df.format(calculatedAt) : "";
        const fieldType = calcSetting ? intl.get(`enum.fieldType.${calcSetting.fieldType}`) : "";
        const preferFarField = calcSetting ? intl.get(`entry.${calcSetting.preferFarField ? "farField" : "nearField"}`) : "";
        const environmentFactor = calcSetting ? nf.format(calcSetting.environmentFactor) : "";

        let progressStr = "";
        if (commonUtils.isBlank(progress) && queueOrder > 0) {
            progressStr = intl.get("entries.queueOrder", { order: queueOrder });
        } else if (!commonUtils.isBlank(progress)) {
            progressStr = `${progress}%`;
        }

        return {
            name, siteNum, username,
            calculatedAt: calculatedAtStr,
            progress: progressStr,
            fieldType,
            preferFarField,
            environmentFactor,
        };
    }, [entry]);

    const calculating = useMemo(() => entry?.calcSetting?.calcAreas?.some(calcArea =>
        calcArea.calculations.some(calculation => !calculation.resultAvailable)
    ) || false, [entry]);

    const shared = user.id !== entry.user.id;

    const onDuplicate = () => entryRest.duplicate(entry.id, true);

    const onCloseSetting = () => setShowWattwaechterDialog(false);

    const onDelete = async () => {
        await entryRest.deleteById(entry.id);
        setShowDeleteConfirmation(false);
    };

    const onCancelCalculations = async () => {
        await calculationRest.cancelEntry(entry.id);
    };

    const onEdit = () => setLocation("/entry/" + entry.id + "/radios");

    const buildErrorMessage = () => {
        const errorMessages = [];
        for (const calcError of entry.calcErrors) {
            errorMessages.push(<div key={calcError.calcAreaId}>  {calcError.calcAreaName + ": " + calcError.message}  </div>);
        }
        return errorMessages;
    };

    const saveEntry = async () => {
        try {
            await entryRest.update(entry);
        } catch (e) {
            commonUtils.dispatchEvent("exception", { i18nLabel: "validateEntry" });
            throw e;
        }
    };

    return (
        <>
            <tr>
                <MenuCellStyle>
                    <div>
                        <DropdownCaret>
                            <Option svgIcon={mdiPencil}
                                onClick={() => onEdit()}
                                disabled={calculating || shared}>
                                {intl.get("common.edit")}
                            </Option>
                            <Option svgIcon={mdiContentDuplicate}
                                onClick={() => onDuplicate()}
                                disabled={calculating}>
                                {intl.get("common.duplicate")}
                            </Option>
                            <Option svgIcon={mdiCalculator}
                                onClick={() => setShowWattwaechterDialog(true)}
                                disabled={calculating || shared}>
                                {intl.get("entry.calculate")}
                            </Option>
                            <Option svgIcon={mdiCloseCircle}
                                onClick={() => onCancelCalculations()}
                                disabled={!calculating || shared}>
                                {intl.get("common.cancel")}
                            </Option>
                            <Option svgIcon={mdiDelete}
                                onClick={() => setShowDeleteConfirmation(true)}
                                disabled={calculating || shared}>
                                {intl.get("common.delete")}
                            </Option>
                        </DropdownCaret>
                        {entry.calcErrors.length > 0 &&
                            <Tooltip message={buildErrorMessage()}>
                                <Svg path={mdiAlertOutline} size="16" />
                            </Tooltip>
                        }
                    </div>
                </MenuCellStyle>
                <RightCellStyle>{formatted.siteNum}</RightCellStyle>
                <LeftCellStyle>{formatted.name}</LeftCellStyle>
                <LeftCellStyle>{formatted.username}</LeftCellStyle>
                <LeftCellStyle>{formatted.calculatedAt}</LeftCellStyle>
                <RightCellStyle>{formatted.progress}</RightCellStyle>
                <LeftCellStyle>{formatted.fieldType}</LeftCellStyle>
                <LeftCellStyle>{formatted.preferFarField}</LeftCellStyle>
                <LeftCellStyle>{formatted.environmentFactor}</LeftCellStyle>
            </tr >
            {showWattwaechterDialog &&
                <EntryProvider entry={entry}>
                    <ValidationProvider>
                        <WattwaechterDialog onClose={onCloseSetting} saveEntry={saveEntry} />
                    </ValidationProvider>
                </EntryProvider>
            }
            {showDeleteConfirmation &&
                <Confirm
                    title={intl.get("common.delete")}
                    body={intl.get("entry.deleteConfirmation", { name: entry.name })}
                    confirm={intl.get("common.delete")}
                    onConfirm={() => onDelete()}
                    onClose={() => setShowDeleteConfirmation(false)} />
            }
        </>
    );
};